import { useQuery } from '@powersync/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';

import { Button } from '@/components/ui/Button';
import { Divider } from '@/components/ui/Divider';
import { Sheet } from '@/components/ui/Sheet';
import { Text } from '@/components/ui/Text';
import {
  createTemplateShare,
  removeTemplateCollaborator,
  revokeTemplateShare,
  setCollaboratorRole,
  templateRoster,
  templateShareList,
  type ShareLink,
} from '@/lib/data';
import { timeAgo } from '@/lib/format';
import { templateShareUrl } from '@/lib/links';
import { shareLink } from '@/lib/share-sheet';
import { useAsyncAction } from '@/lib/useAsyncAction';
import { type TemplateRole } from '@rbf/shared';

import { AccessRoster } from './AccessRoster';
import { ROLE_META, RolePicker } from './RolePicker';

export interface ShareSheetProps {
  visible: boolean;
  onClose: () => void;
  /** The template to manage sharing for. The sheet fetches its own data. */
  templateId: string;
}

/**
 * Manage a template's invite links: mint any number of role-tagged links, copy
 * or revoke each independently, and manage who has access (the roster below).
 * Self-contained — it reads the template header via `useQuery` and owns the
 * links flow. Redeeming a link grants access to the template and all its runs.
 */
export function ShareSheet({ visible, onClose, templateId }: ShareSheetProps) {
  const { data: templates } = useQuery<{ title: string }>(
    'SELECT title FROM templates WHERE id = ?',
    [templateId],
  );
  const { data: stepRows } = useQuery<{ n: number }>(
    'SELECT COUNT(*) AS n FROM template_items WHERE template_id = ?',
    [templateId],
  );
  const templateTitle = templates[0]?.title ?? '';
  const stepCount = stepRows[0]?.n ?? 0;

  const [newRole, setNewRole] = useState<TemplateRole>('member');
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { busy: creating, error, setError, run } = useAsyncAction();

  // Copy the link and, on web (where shareLink writes to the clipboard rather
  // than opening a native sheet), flip that link's Copy button to "Copied!" for
  // a beat as confirmation.
  const copyLink = useCallback(async (token: string) => {
    const result = await shareLink(templateShareUrl(token));
    if (result !== 'copied') return;
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    setCopiedToken(token);
    copiedTimer.current = setTimeout(() => setCopiedToken(null), 1800);
  }, []);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  const loadLinks = useCallback(async () => {
    try {
      const list = await templateShareList(templateId);
      setLinks(list);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [templateId]);

  // Refetch the outstanding links each time the sheet opens (they're admin-only
  // and off the sync stream, so they come from an RPC, not a live query). The
  // previous list stays visible until the fetch resolves — no null flash.
  useEffect(() => {
    if (!visible) return;
    void (async () => {
      await loadLinks();
    })();
  }, [visible, loadLinks]);

  async function createLink() {
    await run(async () => {
      const token = await createTemplateShare(templateId, newRole);
      await loadLinks();
      await copyLink(token);
    });
  }

  async function revokeLink(link: ShareLink) {
    setRevokingId(link.id);
    setError(null);
    try {
      await revokeTemplateShare(link.id);
      await loadLinks();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <Sheet visible={visible} onClose={onClose} title="Share template">
      <Text variant="accent" className="mb-4">
        {templateTitle} · {stepCount} steps
      </Text>

      <Text variant="label" className="mb-2">
        Create an invite link
      </Text>
      <View className="gap-3">
        <RolePicker value={newRole} onChange={setNewRole} disabled={creating} />
        <Button block label="Create link" busy={creating} onPress={createLink} />
      </View>

      <Divider className="my-4" />

      <Text variant="label" className="mb-2">
        Invite links
      </Text>
      {links === null ? (
        <Text variant="caption" className="text-faint">
          Loading…
        </Text>
      ) : links.length === 0 ? (
        <Text variant="caption" className="text-faint">
          No links yet. Create one above and share it.
        </Text>
      ) : (
        <View className="gap-2">
          {links.map((link) => (
            <View
              key={link.id}
              className="gap-2 rounded-xl border border-border bg-card px-3 py-3"
            >
              <View className="flex-row items-center justify-between gap-2">
                <View className="flex-1">
                  <Text variant="bodySemibold">{ROLE_META[link.role].label}</Text>
                  {/* Links never expire, so surface age as a reminder to revoke
                      stale ones. */}
                  <Text variant="accentSmall" className="text-faint">
                    Created {timeAgo(link.created_at)}
                  </Text>
                </View>
                <View className="flex-row gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    label={
                      Platform.OS === 'web'
                        ? copiedToken === link.token
                          ? 'Copied!'
                          : 'Copy'
                        : 'Share'
                    }
                    onPress={() => copyLink(link.token)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    label="Revoke"
                    busy={revokingId === link.id}
                    onPress={() => revokeLink(link)}
                  />
                </View>
              </View>
              <Text variant="accentSmall" numberOfLines={1} className="text-faint">
                {templateShareUrl(link.token)}
              </Text>
            </View>
          ))}
        </View>
      )}

      <Divider className="my-4" />

      <AccessRoster
        title="People with access"
        templateId={templateId}
        load={() => templateRoster(templateId)}
        onRemove={(m) => removeTemplateCollaborator(templateId, m.user_id)}
        onChangeRole={(m, r) => setCollaboratorRole(templateId, m.user_id, r)}
        reloadKey={visible}
      />

      {error ? (
        <Text variant="caption" className="mt-4 text-alert">
          {error}
        </Text>
      ) : null}
    </Sheet>
  );
}
